import { useEffect } from "react";
import { Link } from "react-router-dom";
import { AREAS } from "@/lib/areas";
import PageHero from "@/components/PageHero";
import CTABanner from "@/components/CTABanner";

const LocationsPage = () => {
  useEffect(() => {
    document.title = "Areas We Cover | SeaShells Care · Essex Live-In Care";
    // Schema.org ItemList — tells Google this is a curated list of locations
    const schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Areas SeaShells Care covers across Essex",
      itemListElement: AREAS.map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: `Live-in care in ${a.name}`,
        url: `https://seashellcare.com/live-in-care/${a.slug}`,
      })),
    };
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.setAttribute("data-locations-schema", "true");
    tag.textContent = JSON.stringify(schema);
    document.head.appendChild(tag);
    return () => document.head.removeChild(tag);
  }, []);

  return (
    <main data-testid="page-locations">
      <PageHero
        eyebrow="Areas we cover"
        title="Premium live-in care across Epping Forest, Harlow & beyond."
        subtitle="We're deliberately local. We only deliver in-home care for elders in the Essex towns and villages we know — because knowing the street, the GP surgery and the local pharmacy matters."
      />

      <section className="section">
        <div className="container-px max-w-7xl mx-auto">
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {AREAS.map((area) => (
              <Link
                key={area.slug}
                to={`/live-in-care/${area.slug}`}
                data-testid={`locations-card-${area.slug}`}
                className="group relative rounded-3xl border border-[var(--ss-line)] bg-white p-7 transition-all hover:border-[var(--ss-gold)] hover:shadow-[0_18px_40px_-22px_rgba(31,78,74,0.25)] hover:-translate-y-1"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.22em] text-[var(--ss-gold-dark)]">
                    {area.postcode}
                  </span>
                  <i className="fa-solid fa-arrow-right text-[var(--ss-teal-700)] transition-transform group-hover:translate-x-1"></i>
                </div>
                <h2 className="font-display text-3xl text-[var(--ss-teal-800)] mt-3">
                  {area.name}
                </h2>
                <p className="mt-3 text-sm text-[var(--ss-ink-soft)] leading-relaxed line-clamp-4">
                  {area.intro}
                </p>
                <div className="mt-5 inline-flex items-center gap-2 text-[var(--ss-teal-700)] font-semibold text-sm">
                  Live-in care in {area.name}
                  <i className="fa-solid fa-arrow-right text-[10px]"></i>
                </div>
              </Link>
            ))}
          </div>

          {/* Helpful note for towns not in the list */}
          <div className="mt-12 text-center max-w-2xl mx-auto">
            <p className="text-[var(--ss-ink-soft)]">
              Not seeing your town?  Pick up the phone and we'll tell you in 30
              seconds whether we cover your postcode. We've placed live-in carers
              all across the CM and EN postcodes.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
              <Link to="/contact#enquiry-form" className="btn btn-primary" data-testid="locations-cta-book">
                <i className="fa-regular fa-calendar-check"></i>
                Book a free assessment
              </Link>
              <a href="tel:+441992892894" className="btn btn-outline-dark" data-testid="locations-cta-call">
                <i className="fa-solid fa-phone"></i>
                Call 01992 892894
              </a>
            </div>
          </div>
        </div>
      </section>

      <CTABanner
        eyebrow="Local care, properly"
        heading="The same trusted carer, in their own home."
        body="Wherever in Essex you are — let's talk about what your family needs."
        variant="cream"
      />
    </main>
  );
};

export default LocationsPage;
